import { Component, Input, Output, EventEmitter, SimpleChanges, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Task } from '@app/_components/task';
import { TaskService } from '@app/_components/task';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TaskComponent } from '../task/task.component';
import { BaseTaskViewComponent } from '../base-task-view.component';
import { first } from 'rxjs/operators';
import { Location } from '@angular/common';

interface ColumnPaginationState {
  page: number;
  pageSize: number;
  totalRecords: number;
  hasMore: boolean;
  loading: boolean;
  loadedTasks: Task[];
  allTasksLoaded: boolean; // Track if all tasks for this column have been loaded
}

@Component({
    selector: 'app-task-board-view',
    templateUrl: './task-board-view.component.html',
    styleUrls: ['./task-board-view.component.css'],
    standalone: false
})
export class TaskBoardViewComponent extends BaseTaskViewComponent implements AfterViewInit, OnDestroy {
  @Input() groupBy: string = 'status';
  @Output() taskCreated = new EventEmitter<void>();
  @ViewChild('boardContainer', { static: false }) boardContainer: ElementRef;

  // Board columns (statuses)
  columns: any[] = [];
  tasksByColumn: Map<any, Task[]> = new Map();
  
  // Per-column pagination state
  columnPagination: Map<any, ColumnPaginationState> = new Map();
  
  // Initial page size for tasks
  private readonly initialPageSize: number = 5;
  
  // Global loading state
  loading: boolean = false;
  allLoadedTasks: Task[] = []; // Store all loaded tasks for reference

  // Context menu
  items: any[] = [];
  
  // Store scroll positions for each column before reload
  private columnScrollPositions: Map<any, number> = new Map();
  
  // Store overall board scroll position
  private boardScrollPosition: number = 0;

  constructor(
    authenticationService: AuthenticationService,
    taskService: TaskService,
    userService: UserService,
    alertService: AlertService,
    dialog: MatDialog,
    location: Location
  ) {
    super(authenticationService, taskService, userService, alertService, dialog, location);
  }

  protected onInitView(): void {
    this.initializeColumns();
    this.initializeColumnPagination();
    this.initializeContextMenu();
  }

  private initializeContextMenu(): void {
    this.items = [
      {
        label: 'Copy Task',
        icon: 'pi pi-fw pi-copy',
        command: (event) => this.openTaskForClone()
      },
      {
        label: 'Sub Task',
        icon: 'pi pi-fw pi-clone',
        command: (event) => this.openTaskForSubTask()
      },
      {
        label: 'Mail Task',
        icon: 'pi pi-fw pi-envelope',
        command: (event) => this.openMailTask()
      },
      {
        label: 'Summary',
        icon: 'pi pi-fw pi-search',
        command: (event) => this.openSummary()
      },
      {
        label: 'Status Update req',
        icon: 'pi pi-question',
        command: (event) => this.sendStatusUpdateReq(this.selectedTask)
      },
    ];
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadAllColumns(), 100);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  protected onViewChanges(changes: SimpleChanges): void {
    if (changes['groupBy']) {
      this.initializeColumns();
      this.initializeColumnPagination();
    }
    if (changes['filters'] || changes['groupBy'] || changes['taskTag']) {
      this.resetAllPagination();
      setTimeout(() => this.loadAllColumns(), 100);
    } else if (changes['tasks'] && this.allLoadedTasks.length === 0) {
      setTimeout(() => this.loadAllColumns(), 100);
    }
  }

  private initializeColumnPagination(): void {
    this.columnPagination.clear();
    const columnKeys = this.getColumnKeys();
    columnKeys.forEach(key => {
      this.columnPagination.set(key, {
        page: 0,
        pageSize: this.initialPageSize,
        totalRecords: 0,
        hasMore: true,
        loading: false,
        loadedTasks: [],
        allTasksLoaded: false
      });
    });
  }

  private resetAllPagination(): void {
    this.columnPagination.forEach(state => {
      state.page = 0;
      state.totalRecords = 0;
      state.hasMore = true;
      state.loadedTasks = [];
      state.allTasksLoaded = false;
      state.loading = false;
    });
    this.allLoadedTasks = [];
    this.tasksByColumn.clear();
  }

  private loadAllColumns(): void {
    this.getColumnKeys().forEach(key => this.loadTasksForColumn(key));
  }

  private loadTasksForColumn(columnKey: any): void {
    const paginationState = this.columnPagination.get(columnKey);
    if (!paginationState || paginationState.loading || paginationState.allTasksLoaded) {
      return;
    }

    // Prevent loading if we've already determined there are no tasks
    // (totalRecords is 0 and we've already tried loading)
    if (paginationState.totalRecords === 0 && paginationState.page > 0 && paginationState.loadedTasks.length === 0) {
      paginationState.allTasksLoaded = true;
      return;
    }

    paginationState.loading = true;

    // Prepare filters for API call
    const apiFilters: any = {};
    
    // Convert filters to API format
    // Note: We exclude the groupBy field from global filters since we'll set it per column
    if (this.filters) {
      if (this.filters.taskId && Array.isArray(this.filters.taskId)) {
        apiFilters.taskId = { value: this.filters.taskId, matchMode: 'in' };
      }
      if (this.filters.name) {
        apiFilters.name = { value: this.filters.name, matchMode: 'contains' };
      }
      // Only add status filter if groupBy is NOT status
      if (this.groupBy !== 'status' && this.filters.status && Array.isArray(this.filters.status) && this.filters.status.length > 0) {
        apiFilters.status = { value: this.filters.status, matchMode: 'in' };
      }
      // Only add priority filter if groupBy is NOT priority
      if (this.groupBy !== 'priority' && this.filters.priority && Array.isArray(this.filters.priority) && this.filters.priority.length > 0) {
        apiFilters.priority = { value: this.filters.priority, matchMode: 'in' };
      }
      // Add assignedTo filter
      if (this.filters.assignedTo) {
        if (Array.isArray(this.filters.assignedTo)) {
          const userIds = this.filters.assignedTo.map(u => u.userId || u);
          apiFilters['assignedToUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['assignedToUserVO.userId'] = { value: [this.filters.assignedTo.userId], matchMode: 'in' };
        }
      }
      if (this.filters.createdBy) {
        if (Array.isArray(this.filters.createdBy)) {
          const userIds = this.filters.createdBy.map(u => u.userId || u);
          apiFilters['createdByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['createdByUserVO.userId'] = { value: [this.filters.createdBy.userId], matchMode: 'in' };
        }
      }
      if (this.filters.requestedBy) {
        if (Array.isArray(this.filters.requestedBy)) {
          const userIds = this.filters.requestedBy.map(u => u.userId || u);
          apiFilters['requestedByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['requestedByUserVO.userId'] = { value: [this.filters.requestedBy.userId], matchMode: 'in' };
        }
      }
    }

    // Add column-specific filter based on groupBy
    // This overrides any global filter for the groupBy field
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      apiFilters.status = { value: [columnKey], matchMode: 'in' };
    } else if (this.groupBy === 'priority') {
      apiFilters.priority = { value: [columnKey], matchMode: 'in' };
    }

    // Add taskTag filter if present
    if (this.taskTag) {
      apiFilters.tagList = { value: this.taskTag, matchMode: 'in' };
    }

    // Default sort
    const multiSortMeta = [
      { field: 'dateDue', order: -1 },
      { field: 'dateCreated', order: 0 }
    ];

    this.taskService.findByFilterSummaryWithDateRange(
      paginationState.page,
      paginationState.pageSize,
      null,
      null,
      multiSortMeta,
      null,
      apiFilters,
      null
    ).pipe(first()).subscribe(
      apiResponse => {
        const newTasks = apiResponse.data.tasks || [];
        paginationState.totalRecords = apiResponse.page?.recordTotal || 0;

        // Flatten tasks including children and remove duplicates by taskId
        const flattenedNewTasks: Task[] = [];
        const taskIdSet = new Set<number>();
        newTasks.forEach(task => {
          // Add parent task if not already added
          if (task.taskId && !taskIdSet.has(task.taskId)) {
            flattenedNewTasks.push(task);
            taskIdSet.add(task.taskId);
          }
          // Add children if they exist and haven't been added
          if (task.children && task.children.length > 0) {
            task.children.forEach(child => {
              if (child.taskId && !taskIdSet.has(child.taskId)) {
                flattenedNewTasks.push(child);
                taskIdSet.add(child.taskId);
              }
            });
          }
        });

        // Append new tasks to column's loaded tasks, avoiding duplicates
        const existingTaskIds = new Set(paginationState.loadedTasks.map(t => t.taskId));
        const uniqueNewTasks = flattenedNewTasks.filter(task => 
          task.taskId && !existingTaskIds.has(task.taskId)
        );
        paginationState.loadedTasks = [...paginationState.loadedTasks, ...uniqueNewTasks];
        
        // Update allLoadedTasks for reference
        const allLoadedTaskIds = new Set(this.allLoadedTasks.map(t => t.taskId));
        uniqueNewTasks.forEach(task => {
          if (task.taskId && !allLoadedTaskIds.has(task.taskId)) {
            this.allLoadedTasks.push(task);
            allLoadedTaskIds.add(task.taskId);
          }
        });

        // Check if there are more tasks to load for this column
        const totalLoaded = paginationState.loadedTasks.length;
        
        // Handle empty results
        if (flattenedNewTasks.length === 0) {
          // No new tasks returned - mark as all loaded
          paginationState.hasMore = false;
          paginationState.allTasksLoaded = true;
        } else {
          // Check if we've loaded all available tasks
          paginationState.hasMore = totalLoaded < paginationState.totalRecords;
          paginationState.allTasksLoaded = !paginationState.hasMore;
        }

        // If totalRecords is 0 and we have no tasks, mark as all loaded
        if (paginationState.totalRecords === 0 && totalLoaded === 0) {
          paginationState.hasMore = false;
          paginationState.allTasksLoaded = true;
        }

        // Update tasks for this column
        this.tasksByColumn.set(columnKey, [...paginationState.loadedTasks]);

        // Emit that filters have been processed
        setTimeout(() => {
          this.filtersProcessed.emit();
        }, 100);

        paginationState.loading = false;
      },
      error => {
        this.alertService.error(error);
        paginationState.hasMore = false;
        paginationState.allTasksLoaded = true;
        paginationState.loading = false;
        this.filtersProcessed.emit();
      }
    );
  }

  loadMoreTasksForColumn(columnKey: any): void {
    const paginationState = this.columnPagination.get(columnKey);
    if (!paginationState || paginationState.loading || paginationState.allTasksLoaded) {
      return;
    }

    paginationState.page++;
    this.loadTasksForColumn(columnKey);
  }

  // Listen to scroll on individual column content (for vertical scrolling)
  onColumnScroll(event: Event, columnKey: any): void {
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    const paginationState = this.columnPagination.get(columnKey);
    if (!paginationState || paginationState.loading || paginationState.allTasksLoaded) {
      return;
    }

    // If there are no tasks loaded and totalRecords is 0, don't try to load more
    if (paginationState.loadedTasks.length === 0 && paginationState.totalRecords === 0) {
      return;
    }

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Only trigger if scrollHeight is greater than clientHeight (content is scrollable)
    // This prevents infinite scroll when content is empty or very short
    if (scrollHeight <= clientHeight) {
      return;
    }

    // Load more when scrolled to within 200px of bottom
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      this.loadMoreTasksForColumn(columnKey);
    }
  }

  initializeColumns() {
    // Get columns based on groupBy
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      this.columns = this.taskService.statuses || [];
    } else if (this.groupBy === 'priority') {
      this.columns = this.taskService.priorities || [];
    } else {
      // Default to status
      this.columns = this.taskService.statuses || [];
    }
  }


  getTasksForColumn(key: any): Task[] {
    return this.tasksByColumn.get(key) || [];
  }

  getColumnLoadingState(key: any): boolean {
    const state = this.columnPagination.get(key);
    return state ? state.loading : false;
  }

  getColumnHasMore(key: any): boolean {
    const state = this.columnPagination.get(key);
    return state ? state.hasMore : false;
  }

  getColumnAllTasksLoaded(key: any): boolean {
    const state = this.columnPagination.get(key);
    return state ? state.allTasksLoaded : false;
  }

  getColumnTotalRecords(key: any): number {
    const state = this.columnPagination.get(key);
    return state ? state.totalRecords : 0;
  }

  getColumnName(key: any): string {
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      const column = this.columns.find(col => col.value === key);
      return column ? column.name : `Status ${key}`;
    } else if (this.groupBy === 'priority') {
      const column = this.columns.find(col => col.value === key);
      return column ? column.name : `Priority ${key}`;
    }
    return String(key);
  }

  getColumnKeys(): any[] {
    return this.columns.map(col => col.value);
  }


  getTaskColor(task: Task): string {
    if (!task.dateDue) return '#6c757d';
    
    const dueDate = new Date(task.dateDue);
    const today = new Date();
    const daysDiff = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if ((task as any).status === 2) { // Completed
      return '#28a745';
    } else if (daysDiff < 0) {
      return '#dc3545'; // Overdue
    } else if (daysDiff === 0) {
      return '#ffc107'; // Due today
    } else if (daysDiff <= 3) {
      return '#fd7e14'; // Due soon
    } else {
      return '#17a2b8'; // Normal
    }
  }

  onAddTaskClick(columnKey: any, event: Event) {
    event.stopPropagation();
    
    let taskApi: any = {};
    
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      // Column key is the status value
      taskApi.status = columnKey;
    } else if (this.groupBy === 'priority') {
      // Column key is the priority value
      taskApi.priority = columnKey;
    }
    
    // Open task creation dialog
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.maxWidth = "90vw";
    dialogConfig.width = "80vw";
    dialogConfig.data = {
      title: 'Add Task',
      isModal: true,
      taskApi: taskApi,
      selectedTagForCreate: this.taskTag
    };
    
    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
    
    // Subscribe to task creation event - wait for component to initialize
    setTimeout(() => {
      if (dialogRef.componentInstance) {
        dialogRef.componentInstance.doAdd.subscribe((data: any) => {
          console.log('Task created:', data);
          dialogRef.close();
          // Add the new task directly to the appropriate column without reloading all tasks
          this.addTaskToColumn(data, columnKey);
          // Emit event to parent
          this.taskCreated.emit();
        });
        // Handle auto-save updates (keeps dialog open)
        dialogRef.componentInstance.doUpdateAndDontClose.subscribe((data: any) => {
          console.log('Task auto-saved:', data);
          // Add/update the task without closing the dialog
          if (data && data.taskId) {
            this.addTaskToColumn(data, columnKey);
          }
        });
      }
    }, 100);
    
    dialogRef.afterClosed().subscribe(result => {
      // If result is a task object, add it directly
      if (result && result.taskId) {
        this.addTaskToColumn(result, columnKey);
        this.taskCreated.emit();
      }
    });
  }

  addTask(tasktypeId: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.data = {
      title: 'Add Task',
      taskTypeId: tasktypeId,
      isModal: true,
      selectedTagForCreate: this.taskTag
    };

    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);

    // Subscribe to task creation event - wait for component to initialize
    setTimeout(() => {
      if (dialogRef.componentInstance) {
        dialogRef.componentInstance.doAdd.subscribe(
          data => {
            console.log('Dialog output:', data);
            dialogRef.close();
            // Add the new task directly without reloading all tasks
            if (data && data.taskId) {
              this.addTaskToColumnDirectly(data);
            } else {
              // Fallback: reload if task data is not available
              this.reloadTasks();
            }
            this.taskCreated.emit();
          });
        dialogRef.componentInstance.doUpdate.subscribe(
          data => {
            console.log('Dialog output:', data);
            // For updates, we need to find and update the existing task
            if (data && data.taskId) {
              this.updateTaskInColumn(data);
            } else {
              // Fallback: reload if task data is not available
              this.reloadTasks();
            }
            // Close dialog when save button is clicked (doUpdate means manual save)
            dialogRef.close();
            this.taskCreated.emit();
          });
        // Handle auto-save updates (keeps dialog open)
        dialogRef.componentInstance.doUpdateAndDontClose.subscribe(
          data => {
            console.log('Task auto-saved:', data);
            // Add/update the task without closing the dialog
            if (data && data.taskId) {
              // Check if task already exists (it was just created via auto-save)
              const existingTaskIndex = this.allLoadedTasks.findIndex(t => t.taskId === data.taskId);
              if (existingTaskIndex >= 0) {
                this.updateTaskInColumn(data);
              } else {
                this.addTaskToColumnDirectly(data);
              }
            }
          });
      }
    }, 100);
    
    dialogRef.afterClosed().subscribe(result => {
      // If result is a task object, add it directly
      if (result && result.taskId) {
        this.addTaskToColumnDirectly(result);
        this.taskCreated.emit();
      }
    });
  }

  /**
   * Reload tasks from the API to refresh the board view
   * This is called after task creation/update to show the latest tasks
   * Preserves scroll position after reload
   */
  reloadTasks(): void {
    this.saveScrollPositions();
    this.resetAllPagination();
    setTimeout(() => {
      this.loadAllColumns();
      setTimeout(() => this.restoreScrollPositions(), 300);
    }, 100);
  }
  
  /**
   * Save scroll positions for all columns and the board container
   */
  private saveScrollPositions(): void {
    // Save board container scroll position
    if (this.boardContainer?.nativeElement) {
      this.boardScrollPosition = this.boardContainer.nativeElement.scrollLeft || 0;
    }
    
    // Save each column's scroll position
    const columnElements = document.querySelectorAll('.column-content');
    columnElements.forEach((columnEl: Element) => {
      const columnKey = columnEl.getAttribute('data-key');
      if (columnKey) {
        const scrollTop = (columnEl as HTMLElement).scrollTop || 0;
        this.columnScrollPositions.set(columnKey, scrollTop);
      }
    });
  }
  
  /**
   * Restore scroll positions for all columns and the board container
   */
  private restoreScrollPositions(): void {
    // Restore board container scroll position
    if (this.boardContainer?.nativeElement) {
      this.boardContainer.nativeElement.scrollLeft = this.boardScrollPosition;
    }
    
    // Restore each column's scroll position
    const columnElements = document.querySelectorAll('.column-content');
    columnElements.forEach((columnEl: Element) => {
      const columnKey = columnEl.getAttribute('data-key');
      if (columnKey && this.columnScrollPositions.has(columnKey)) {
        const scrollTop = this.columnScrollPositions.get(columnKey) || 0;
        (columnEl as HTMLElement).scrollTop = scrollTop;
      }
    });
  }
  
  /**
   * Override onTaskDoubleClick to open task dialog and reload on close
   */
  override onTaskDoubleClick(task: Task, event: Event): void {
    event.stopPropagation();
    
    // Change URL to match list view behavior
    if (task && task.taskId) {
      this.location.go('/task-view/' + task.taskId);
    }
    
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.data = {
      task: task,
      title: '',
      taskApi: task,
      isModal: true
    };
    
    const dialogRef = this.dialog.open(TaskComponent, dialogConfig);
    
    // Handle task updates
    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.doUpdate.subscribe(
        data => {
          console.log('Task updated:', data);
          this.location.go('/');
          // Reload tasks to ensure board reflects all changes (status/priority changes, etc.)
          this.reloadTasks();
        });
      dialogRef.componentInstance.doAdd.subscribe(
        data => {
          console.log('Task added:', data);
          this.location.go('/');
          // Reload tasks to show the new task
          this.reloadTasks();
        });
      dialogRef.componentInstance.doUpdateAndDontClose.subscribe(
        data => {
          console.log('Task auto-saved:', data);
          // For auto-save, update in place to avoid disrupting the user
          if (data && data.taskId) {
            this.updateTaskInColumn(data);
          }
        });
    }
    
    // Reload tasks when dialog closes, preserving scroll position
    dialogRef.afterClosed().subscribe(result => {
      // Reset URL when dialog closes
      this.location.go('/');
      // Reload tasks while preserving scroll position
      this.reloadTasks();
    });
  }

  /**
   * Add a newly created task directly to the appropriate column
   * This avoids reloading all tasks and only updates the relevant column
   */
  private addTaskToColumn(newTask: any, columnKey: any): void {
    if (!newTask || !newTask.taskId) {
      // If task data is not available, fallback to full reload
      this.reloadTasks();
      return;
    }

    // Get or create pagination state for this column
    let paginationState = this.columnPagination.get(columnKey);
    if (!paginationState) {
      paginationState = {
        page: 0,
        pageSize: this.initialPageSize,
        totalRecords: 0,
        hasMore: true,
        loading: false,
        loadedTasks: [],
        allTasksLoaded: false
      };
      this.columnPagination.set(columnKey, paginationState);
    }

    // Check if task already exists (avoid duplicates)
    const existingTaskIndex = paginationState.loadedTasks.findIndex(t => t.taskId === newTask.taskId);
    if (existingTaskIndex >= 0) {
      // Task already exists, just update it
      paginationState.loadedTasks[existingTaskIndex] = newTask as Task;
    } else {
      // Add new task to the column's loaded tasks
      paginationState.loadedTasks.push(newTask as Task);
      paginationState.totalRecords++;
    }

    // Also update allLoadedTasks for reference
    const existingInAll = this.allLoadedTasks.findIndex(t => t.taskId === newTask.taskId);
    if (existingInAll >= 0) {
      this.allLoadedTasks[existingInAll] = newTask as Task;
    } else {
      this.allLoadedTasks.push(newTask as Task);
    }

    // Update tasks for this column
    this.tasksByColumn.set(columnKey, [...paginationState.loadedTasks]);
  }

  /**
   * Add a newly created task directly without knowing the column key
   * Determines the column based on the task's properties
   */
  private addTaskToColumnDirectly(newTask: any): void {
    if (!newTask || !newTask.taskId) {
      // If task data is not available, fallback to full reload
      this.reloadTasks();
      return;
    }

    // Determine the column key based on groupBy
    let columnKey: any;
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      columnKey = (newTask as any).status || 0;
    } else if (this.groupBy === 'priority') {
      columnKey = (newTask as any).priority || 4;
    } else {
      columnKey = 0; // Default
    }

    // Get or create pagination state for this column
    let paginationState = this.columnPagination.get(columnKey);
    if (!paginationState) {
      paginationState = {
        page: 0,
        pageSize: this.initialPageSize,
        totalRecords: 0,
        hasMore: true,
        loading: false,
        loadedTasks: [],
        allTasksLoaded: false
      };
      this.columnPagination.set(columnKey, paginationState);
    }

    // Check if task already exists (avoid duplicates)
    const existingTaskIndex = paginationState.loadedTasks.findIndex(t => t.taskId === newTask.taskId);
    if (existingTaskIndex >= 0) {
      // Task already exists, just update it
      paginationState.loadedTasks[existingTaskIndex] = newTask as Task;
    } else {
      // Add new task to the column's loaded tasks
      paginationState.loadedTasks.push(newTask as Task);
      paginationState.totalRecords++;
    }

    // Also update allLoadedTasks for reference
    const existingInAll = this.allLoadedTasks.findIndex(t => t.taskId === newTask.taskId);
    if (existingInAll >= 0) {
      this.allLoadedTasks[existingInAll] = newTask as Task;
    } else {
      this.allLoadedTasks.push(newTask as Task);
    }

    // Update tasks for this column
    this.tasksByColumn.set(columnKey, [...paginationState.loadedTasks]);
  }

  /**
   * Update an existing task in the board view
   * This is called when a task is updated
   * Handles tasks moving between columns (e.g., status/priority changes)
   */
  private updateTaskInColumn(updatedTask: any): void {
    if (!updatedTask || !updatedTask.taskId) {
      // If task data is not available, fallback to full reload
      this.reloadTasks();
      return;
    }

    // Determine the new column key based on groupBy
    let newColumnKey: any;
    if (this.groupBy === 'status' || !this.groupBy || this.groupBy === 'none') {
      newColumnKey = (updatedTask as any).status || 0;
    } else if (this.groupBy === 'priority') {
      newColumnKey = (updatedTask as any).priority || 4;
    } else {
      newColumnKey = 0; // Default
    }

    // Find the task in all columns to see if it moved
    let oldColumnKey: any = null;
    for (const [colKey, paginationState] of this.columnPagination.entries()) {
      const existingTaskIndex = paginationState.loadedTasks.findIndex(t => t.taskId === updatedTask.taskId);
      if (existingTaskIndex >= 0) {
        oldColumnKey = colKey;
        // Remove task from old column if it moved to a different column
        if (oldColumnKey !== newColumnKey) {
          paginationState.loadedTasks.splice(existingTaskIndex, 1);
          paginationState.totalRecords = Math.max(0, paginationState.totalRecords - 1);
          // Update the column's task list
          this.tasksByColumn.set(oldColumnKey, [...paginationState.loadedTasks]);
        }
        break;
      }
    }

    // Get or create pagination state for the new column
    let paginationState = this.columnPagination.get(newColumnKey);
    if (!paginationState) {
      paginationState = {
        page: 0,
        pageSize: this.initialPageSize,
        totalRecords: 0,
        hasMore: true,
        loading: false,
        loadedTasks: [],
        allTasksLoaded: false
      };
      this.columnPagination.set(newColumnKey, paginationState);
    }

    // Add or update the task in the new column
    const existingTaskIndex = paginationState.loadedTasks.findIndex(t => t.taskId === updatedTask.taskId);
    if (existingTaskIndex >= 0) {
      // Update existing task
      paginationState.loadedTasks[existingTaskIndex] = updatedTask as Task;
    } else {
      // Task not found in this column, add it
      paginationState.loadedTasks.push(updatedTask as Task);
      if (oldColumnKey !== newColumnKey) {
        // Only increment if it's a new addition (not just moved)
        paginationState.totalRecords++;
      }
    }

    // Also update allLoadedTasks for reference
    const existingInAll = this.allLoadedTasks.findIndex(t => t.taskId === updatedTask.taskId);
    if (existingInAll >= 0) {
      this.allLoadedTasks[existingInAll] = updatedTask as Task;
    } else {
      this.allLoadedTasks.push(updatedTask as Task);
    }

    // Update tasks for the new column
    this.tasksByColumn.set(newColumnKey, [...paginationState.loadedTasks]);
  }
}
